import { useEffect, useRef } from 'react';
import { API_ENDPOINTS } from '../config';
import { getToken } from '../utils/auth';
import type { WhatsAppMessageRecord } from '../types';

export type WhatsAppRealtimeEvent =
  | { type: 'NEW_MESSAGE'; lead_id: string; message: WhatsAppMessageRecord }
  | {
      type: 'MESSAGE_STATUS';
      lead_id: string;
      provider_message_id: string;
      client_temp_id?: string | null;
      status: WhatsAppMessageRecord['status'];
    };

const RETRY_DELAYS = [1000, 2000, 5000, 10000, 30000];

export function useWhatsAppWebSocket(
  leadId: string,
  onEvent: (event: WhatsAppRealtimeEvent) => void,
  onReconnect: () => void,
) {
  const onEventRef = useRef(onEvent);
  const onReconnectRef = useRef(onReconnect);

  useEffect(() => {
    onEventRef.current = onEvent;
    onReconnectRef.current = onReconnect;
  }, [onEvent, onReconnect]);

  useEffect(() => {
    if (!leadId) return;

    let socket: WebSocket | null = null;
    let stopped = false;
    let retry = 0;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    const connect = () => {
      if (stopped) return;
      const token = getToken();
      if (!token) return;

      socket = new WebSocket(API_ENDPOINTS.WS.WHATSAPP(leadId, token));
      socket.onmessage = (message) => {
        try {
          const event = JSON.parse(message.data) as WhatsAppRealtimeEvent | { type: 'connected'; lead_id: string };
          if (event.type === 'connected') return;
          onEventRef.current(event);
        } catch {
          // Ignore malformed realtime frames.
        }
      };
      socket.onopen = () => {
        retry = 0;
        onReconnectRef.current();
      };
      socket.onclose = () => {
        socket = null;
        if (stopped) return;
        const delay = RETRY_DELAYS[Math.min(retry, RETRY_DELAYS.length - 1)];
        retry += 1;
        retryTimer = setTimeout(connect, delay);
      };
      socket.onerror = () => socket?.close();
    };

    connect();

    return () => {
      stopped = true;
      if (retryTimer) clearTimeout(retryTimer);
      socket?.close();
    };
  }, [leadId]);
}
